import {
  Card,
  Col,
  Empty,
  Row,
  Statistic,
  Table,
  Typography,
  theme as antdTheme,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { reportsApi, type CollectionTrendMonth } from '@/api/reports';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';
import { useSchoolTheme } from '@/themes/use-theme';
import { useAuthStore } from '@/store/auth';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function CollectionTrendReportPage() {
  const { token } = antdTheme.useToken();
  const { chartColors } = useSchoolTheme();
  const academicYearId = useAuthStore((s) => s.academicYearId);

  const { data, isFetching } = useQuery({
    queryKey: ['reports', 'collection-trend', academicYearId],
    queryFn: () => reportsApi.collectionTrend(academicYearId ? { academicYearId } : {}),
  });

  const chart = (data?.months ?? []).map((m) => ({
    month: dayjs(m.month).format('MMM YY'),
    billed: Number(m.billed),
    collected: Number(m.collected),
    pct: Number(m.collectionPct),
  }));

  const tooltipStyle = {
    background: token.colorBgElevated,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: 8,
  };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Reports / Finance"
        title="Collection vs billing trend"
        description="Per billing month: net billed against what's been collected and what's still outstanding — so you can see whether collection is keeping pace with billing."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/collection-trend.xlsx',
              params: academicYearId ? { academicYearId } : {},
              fileName: 'collection-trend.xlsx',
              disabled: !data,
            }]}
          />
        }
      />

      {data ? (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Card><Statistic title="Total billed" value={PKR(data.totalBilled)} prefix="₨" /></Card>
            </Col>
            <Col xs={12} md={6}>
              <Card><Statistic title="Total collected" value={PKR(data.totalCollected)} prefix="₨" styles={{ content: { color: token.colorSuccess } }} /></Card>
            </Col>
            <Col xs={12} md={6}>
              <Card><Statistic title="Outstanding" value={PKR(data.totalOutstanding)} prefix="₨" styles={{ content: { color: token.colorError } }} /></Card>
            </Col>
            <Col xs={12} md={6}>
              <Card><Statistic title="Collection %" value={`${PKR(data.collectionPct)}%`} styles={{ content: { fontWeight: 700 } }} /></Card>
            </Col>
          </Row>

          <Card title={<Typography.Text strong>Monthly billed vs collected</Typography.Text>}>
            {chart.length === 0 ? (
              <Empty description="No billing in this academic year yet" />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chart} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} vertical={false} />
                  <XAxis dataKey="month" stroke={token.colorTextSecondary} fontSize={12} />
                  <YAxis yAxisId="left" stroke={token.colorTextSecondary} fontSize={12} tickFormatter={(v) => PKR(v as number)} width={80} />
                  <YAxis yAxisId="right" orientation="right" stroke={token.colorTextSecondary} fontSize={12} domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={48} />
                  <Tooltip
                    formatter={(v: number, n) => (n === 'Collection %' ? [`${PKR(v)}%`, n] : [`₨ ${PKR(v)}`, n as string])}
                    contentStyle={tooltipStyle}
                    cursor={{ fill: token.colorFillTertiary }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="billed" name="Billed" fill={chartColors[2 % chartColors.length]} radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="collected" name="Collected" fill={chartColors[3 % chartColors.length]} radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="pct" name="Collection %" stroke={chartColors[0]} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card title="Per month" styles={{ body: { padding: 0 } }}>
            {data.months.length === 0 ? (
              <Empty style={{ padding: 24 }} description="No data" />
            ) : (
              <Table<CollectionTrendMonth>
                rowKey="month"
                size="small"
                pagination={false}
                loading={isFetching}
                dataSource={data.months}
                columns={[
                  { title: 'Month', dataIndex: 'month', render: (m: string) => dayjs(m).format('MMMM YYYY') },
                  { title: 'Billed', dataIndex: 'billed', width: 140, align: 'right', render: (a: string) => `₨ ${PKR(a)}` },
                  { title: 'Collected', dataIndex: 'collected', width: 140, align: 'right', render: (a: string) => <span style={{ color: token.colorSuccess }}>₨ {PKR(a)}</span> },
                  { title: 'Waived', dataIndex: 'waived', width: 130, align: 'right', render: (a: string) => `₨ ${PKR(a)}` },
                  { title: 'Outstanding', dataIndex: 'outstanding', width: 140, align: 'right', render: (a: string) => <span style={{ color: Number(a) > 0 ? token.colorError : undefined }}>₨ {PKR(a)}</span> },
                  { title: 'Collection %', dataIndex: 'collectionPct', width: 120, align: 'right', render: (p: string) => <Typography.Text strong>{PKR(p)}%</Typography.Text> },
                ]}
              />
            )}
          </Card>
        </>
      ) : (
        <Empty />
      )}
    </div>
  );
}
